import { useCallback, useEffect, useState } from 'react';
import { isFeatureEnabled } from '../utils/featureFlags';
import recommendationAPI from '../services/api/recommendationClient';

/** How often the badge re-checks. Findings are produced on a cron measured in
 *  hours, so a minute is already far finer than the data changes. */
const POLL_MS = 60_000;

/** Both directions of the same conversation with the Inbox page. */
export const INBOX_UNREAD_EVENT = 'saf3ai:inbox-unread-changed';

/**
 * Move the badge by a known amount, right now.
 *
 * Reading something is a local, certain change — waiting for a round trip and
 * a re-query to reflect it makes the badge look stuck, which is the one thing
 * an unread count must never look like.
 */
export const bumpInboxUnread = (delta, module) => {
  window.dispatchEvent(new CustomEvent(INBOX_UNREAD_EVENT, { detail: { delta, module } }));
};

/** Ask for the authoritative count — after a write has actually landed. */
export const refreshInboxUnread = () => {
  window.dispatchEvent(new CustomEvent(INBOX_UNREAD_EVENT));
};

/**
 * Unread count for the Inbox nav badge.
 *
 * Returns null — not 0 — whenever the number is unknown: the flag is off, the
 * service is unreachable, or the first fetch has not landed. The nav must be
 * able to tell "nothing unread" from "could not ask", because a confident 0 on
 * a broken service is the badge quietly lying about your estate.
 *
 * Refreshes on a slow poll, when the tab regains focus, and when the Inbox
 * itself reports a change. A change carrying a delta is applied immediately
 * and optimistically; a bare one triggers a re-read. The page sends the first
 * on the click and the second once the write is durable, so the number moves
 * under the reader's finger and is still reconciled against the server.
 *
 * @param {string} [module] Count one module's findings only (Code Security's
 *   nav item passes 'sast'). Unscoped, it counts everything, as the Atlas Inbox
 *   does. A scoped badge applies only the nudges for its own module; a nudge
 *   that does not say which module it was for makes it re-read instead.
 */
export default function useInboxUnread(module) {
  const [unread, setUnread] = useState(null);
  const enabled = isFeatureEnabled('RECOMMENDATIONS');

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      // Badge = page chrome: never lets a 403 module_not_licensed lock the page.
      const summary = await recommendationAPI.summary(module ? { module } : {}, { skipModuleLock: true });
      setUnread(typeof summary?.unread === 'number' ? summary.unread : null);
    } catch {
      // A nav item is the wrong place to report an outage: drop the badge and
      // let the page itself say the service is down.
      setUnread(null);
    }
  }, [enabled, module]);

  const onChanged = useCallback((event) => {
    const delta = event?.detail?.delta;
    const from = event?.detail?.module;
    if (typeof delta !== 'number' || (module && !from)) {
      refresh();
      return;
    }
    if (module && from !== module) return;
    // Never below zero: an optimistic nudge that raced a poll must not invent
    // a negative unread count.
    setUnread((current) => (typeof current === 'number' ? Math.max(0, current + delta) : current));
  }, [refresh, module]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener(INBOX_UNREAD_EVENT, onChanged);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(INBOX_UNREAD_EVENT, onChanged);
    };
  }, [enabled, refresh, onChanged]);

  return unread;
}
